import Phaser from "phaser";
import { Palette } from "../ui/palette";
import { makeButton } from "../ui/widgets";
import { registerPortraitAnims } from "../ui/portraits";
import { KIRBY_CALL } from "../data/cutscenes";

/**
 * Kirby's incoming video call, played after the pre-relay dialogue and before the
 * relay level. It is the same video-call framing used in the relay (see
 * RelayScene.showKirbyCall), just bigger and centred, with his words shown below.
 *
 * Flow: fade in on a "Connecting..." panel, then Kirby comes on and speaks his
 * beats (KIRBY_CALL in src/data/cutscenes.ts) one at a time, typewriter-style,
 * advancing on click / space / enter. When the last beat ends (or Skip), it starts
 * the relay. The script + per-line animations live in data so they are easy to tweak.
 */

const W = 960;
const H = 540;
const PANEL = { w: 480, h: 300, cy: 196 }; // the call panel (bigger than the in-level one)
const BOX = { x: 150, y: 388, w: 660, h: 118 }; // speech box below the call
const PAD = 20;
const TYPE_MS = 22;

export class KirbyCallScene extends Phaser.Scene {
  private next = "Relay";
  private kirby?: Phaser.GameObjects.Sprite;
  private box?: Phaser.GameObjects.Rectangle;
  private namePlate?: Phaser.GameObjects.Container;
  private textObj?: Phaser.GameObjects.Text;
  private cue?: Phaser.GameObjects.Text;

  private beatIndex = 0;
  private connecting = true;
  private typing = false;
  private typeEvent?: Phaser.Time.TimerEvent;
  private full = "";
  private shown = 0;

  constructor() {
    super("KirbyCall");
  }

  create(data: { next?: string }) {
    this.next = data?.next ?? "Relay";
    this.beatIndex = 0;
    this.connecting = true;

    // Backdrop (matches the dialogue screen's wash so the cut reads as one piece).
    this.add.rectangle(W / 2, H / 2, W, H, Palette.bg).setDepth(0);
    this.add.rectangle(W / 2, H / 2 - 40, W, 360, 0x141821, 0.6).setDepth(0);

    // The video-call panel.
    const px = W / 2;
    const py = PANEL.cy;
    this.add.rectangle(px, py, PANEL.w, PANEL.h, Palette.panel, 0.94).setDepth(2).setStrokeStyle(3, Palette.kirby);
    this.add
      .text(px - PANEL.w / 2 + 14, py - PANEL.h / 2 + 10, "● Kirby (live)", {
        fontFamily: "monospace",
        fontSize: "13px",
        color: Palette.text,
      })
      .setDepth(4);

    registerPortraitAnims(this);

    // Kirby, anchored to the bottom edge of the panel (webcam framing), scaled to fill.
    this.kirby = this.add.sprite(px, py + PANEL.h / 2 - 6, "kirby-blink", 0).setOrigin(0.5, 1).setDepth(3).setVisible(false);
    this.kirby.setScale((PANEL.h * 0.9) / this.kirby.height);

    // Speech box + name plate + text (hidden until Kirby connects).
    this.box = this.add.rectangle(BOX.x, BOX.y, BOX.w, BOX.h, Palette.panel, 0.96).setOrigin(0, 0).setDepth(2).setStrokeStyle(3, Palette.panelEdge).setVisible(false);
    this.namePlate = this.buildNamePlate(BOX.x + 70, BOX.y, "KIRBY", Palette.kirby).setVisible(false);
    this.textObj = this.add
      .text(BOX.x + PAD, BOX.y + PAD, "", {
        fontFamily: "monospace",
        fontSize: "19px",
        color: Palette.text,
        lineSpacing: 8,
        wordWrap: { width: BOX.w - PAD * 2 },
      })
      .setDepth(4)
      .setVisible(false);
    this.cue = this.add
      .text(BOX.x + BOX.w - 16, BOX.y + BOX.h - 14, "▶", { fontFamily: "monospace", fontSize: "18px", color: Palette.textDim })
      .setOrigin(1, 1)
      .setDepth(5)
      .setAlpha(0);

    makeButton(this, W - 78, 30, "Skip  ⏭", () => this.finish(), { width: 120, height: 36, fontSize: "14px" }).setDepth(50);

    this.input.on("pointerdown", () => this.advance());
    this.input.keyboard?.on("keydown-SPACE", () => this.advance());
    this.input.keyboard?.on("keydown-ENTER", () => this.advance());
    this.input.keyboard?.on("keydown-ESC", () => this.finish());

    this.cameras.main.fadeIn(400, 0, 0, 0);
    this.startConnecting(px, py);
  }

  /** Show a "Connecting..." pulse in the panel, then bring Kirby on. */
  private startConnecting(px: number, py: number) {
    const conn = this.add
      .text(px, py, "Connecting", { fontFamily: "monospace", fontSize: "22px", color: Palette.textDim })
      .setOrigin(0.5)
      .setDepth(4);
    let dots = 0;
    const timer = this.time.addEvent({
      delay: 350,
      loop: true,
      callback: () => {
        dots = (dots + 1) % 4;
        conn.setText("Connecting" + ".".repeat(dots));
      },
    });
    this.time.delayedCall(KIRBY_CALL.connectingMs, () => {
      timer.remove();
      conn.destroy();
      this.connecting = false;
      this.kirby?.setVisible(true);
      this.box?.setVisible(true);
      this.namePlate?.setVisible(true);
      this.textObj?.setVisible(true);
      this.showBeat();
    });
  }

  private showBeat() {
    const beat = KIRBY_CALL.beats[this.beatIndex];
    this.kirby?.play(
      { key: beat.anim, repeat: beat.loop === false ? 0 : -1, repeatDelay: beat.repeatDelayMs ?? 0 },
      true,
    );
    this.full = beat.text;
    this.startTyping();
  }

  private startTyping() {
    this.shown = 0;
    this.typing = true;
    this.cue?.setAlpha(0);
    this.textObj?.setText("");
    this.typeEvent?.remove();
    this.typeEvent = this.time.addEvent({
      delay: TYPE_MS,
      loop: true,
      callback: () => {
        this.shown++;
        this.textObj?.setText(this.full.slice(0, this.shown));
        if (this.shown >= this.full.length) this.finishTyping();
      },
    });
  }

  private finishTyping() {
    this.typeEvent?.remove();
    this.typeEvent = undefined;
    this.typing = false;
    this.textObj?.setText(this.full);
    if (this.cue) {
      this.cue.setAlpha(1);
      this.tweens.add({ targets: this.cue, alpha: 0.2, duration: 600, yoyo: true, repeat: -1 });
    }
  }

  private advance() {
    if (this.connecting) return; // ignore taps while still connecting
    if (this.typing) {
      this.finishTyping();
      return;
    }
    this.beatIndex++;
    if (this.beatIndex >= KIRBY_CALL.beats.length) return this.finish();
    this.tweens.killTweensOf(this.cue!);
    this.showBeat();
  }

  private buildNamePlate(cx: number, boxTop: number, name: string, color: number): Phaser.GameObjects.Container {
    const label = this.add
      .text(0, 0, name.toUpperCase(), { fontFamily: "monospace", fontSize: "16px", color: "#0b0d12", fontStyle: "bold" })
      .setOrigin(0.5);
    const plate = this.add.rectangle(0, 0, label.width + 28, 26, color, 1).setStrokeStyle(2, 0x0b0d12);
    return this.add.container(cx, boxTop, [plate, label]).setDepth(5);
  }

  private finish() {
    this.typeEvent?.remove();
    this.cameras.main.fadeOut(400, 0, 0, 0);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => this.scene.start(this.next));
  }
}
